/**
 * Payment Service
 *
 * Handles payment operations including creating payment links,
 * checking status, and managing payment provider integrations.
 * All provider config is stored in tenant-schema payment_providers table.
 */

import { supabase } from '@/integrations/supabase/client';
import { getErrorMessage } from '@/lib/error-utils';
import type {
  Payment,
  PaymentProvider,
  CreatePaymentRequest,
  CreatePaymentResponse,
} from '@/types/payment-types';

// A-16: this used to build the display URL from VITE_SUPABASE_URL, which is the raw
// Supabase project host -- it leaked the project ref and the tenant's UUID straight to
// the merchant's screen. The real webhook Transbank/Mercado Pago actually call is built
// server-side from API_DOMAIN with a fallback to the product's own domain
// (supabase/functions/_shared/payment-providers/webhook-url.ts). Mirroring that here
// keeps what the merchant sees matching what is actually registered, with the
// infrastructure host gone.
const EDGE_FUNCTION_BASE_URL = `https://${import.meta.env.VITE_API_DOMAIN || 'api.okostec.ai'}/functions/v1`;

// B5/F4: Uses URL constructor with searchParams for proper encoding
export function getWebhookDisplayUrl(
  provider: string,
  tenantId: string,
  schemaName: string,
  suffix = 'webhook'
): string {
  const url = new URL(`${EDGE_FUNCTION_BASE_URL}/${provider}-${suffix}`);
  url.searchParams.set('tenant_id', tenantId);
  url.searchParams.set('schema', schemaName);
  return url.toString();
}

// G2: Provider metadata map — eliminates ternaries and enables easy provider addition
const PROVIDER_METADATA: Record<string, { name: string; type: string; functionName: string }> = {
  transbank:   { name: 'Transbank',    type: 'transbank',    functionName: 'transbank-create-payment' },
  mercadopago: { name: 'Mercado Pago', type: 'mercado_pago', functionName: 'mercadopago-create-payment' },
};

/**
 * Create a payment link using the specified provider
 */
export async function createPaymentLink(
  provider: PaymentProvider,
  data: CreatePaymentRequest
): Promise<CreatePaymentResponse> {
  try {
    const meta = PROVIDER_METADATA[provider];
    const functionName = meta?.functionName || `${provider}-create-payment`;

    const { data: response, error } = await supabase.functions.invoke(functionName, {
      body: data,
    });

    if (error) {
      if (import.meta.env.DEV) console.error(`Error creating ${provider} payment:`, error);
      return {
        success: false,
        error: getErrorMessage(error) || `Failed to create ${provider} payment`,
      };
    }

    return response;
  } catch (error: unknown) {
    if (import.meta.env.DEV) console.error('Error in createPaymentLink:', error);
    return {
      success: false,
      error: getErrorMessage(error) || 'An unexpected error occurred',
    };
  }
}

/**
 * Get payment by ID from tenant schema
 */
export async function getPaymentById(
  tenantId: string,
  paymentId: string
): Promise<Payment | null> {
  try {
    const { data: tenant, error: tenantError } = await supabase
      .from('tenants')
      .select('schema_name')
      .eq('id', tenantId)
      .single();

    if (tenantError || !tenant) {
      if (import.meta.env.DEV) console.error('Tenant not found:', tenantError);
      return null;
    }

    const { data, error } = await supabase.rpc('get_payment_by_id', {
      p_tenant_schema: tenant.schema_name,
      p_payment_id: paymentId,
    });

    if (error) {
      if (import.meta.env.DEV) console.error('Error fetching payment:', error);
      return null;
    }

    // Returns the row JSONB or NULL.
    return data ? (data as Payment) : null;
  } catch (error) {
    if (import.meta.env.DEV) console.error('Error in getPaymentById:', error);
    return null;
  }
}

/**
 * Get all payments for a conversation
 */
export async function getConversationPayments(
  tenantId: string,
  conversationId: string
): Promise<Payment[]> {
  try {
    const { data: tenant, error: tenantError } = await supabase
      .from('tenants')
      .select('schema_name')
      .eq('id', tenantId)
      .single();

    if (tenantError || !tenant) {
      if (import.meta.env.DEV) console.error('Tenant not found:', tenantError);
      return [];
    }

    const { data, error } = await supabase.rpc('list_conversation_payments', {
      p_tenant_schema: tenant.schema_name,
      p_conversation_id: conversationId,
    });

    if (error) {
      if (import.meta.env.DEV) console.error('Error fetching conversation payments:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    if (import.meta.env.DEV) console.error('Error in getConversationPayments:', error);
    return [];
  }
}

/**
 * Get payment provider enabled flags for a tenant (used by chat UI to show payment button)
 */
export async function getPaymentProviderSettings(
  tenantId: string
): Promise<Record<PaymentProvider, boolean>> {
  try {
    const { data: tenant, error: tenantError } = await supabase
      .from('tenants')
      .select('schema_name')
      .eq('id', tenantId)
      .single();

    if (tenantError || !tenant) {
      if (import.meta.env.DEV) console.error('Error fetching tenant:', tenantError);
      return { transbank: false, mercadopago: false };
    }

    const { data: providers, error } = await supabase.rpc('get_tenant_payment_providers', {
      p_tenant_schema: tenant.schema_name,
    });

    if (error) {
      if (import.meta.env.DEV) console.error('Error fetching payment providers:', error);
      return { transbank: false, mercadopago: false };
    }

    // G2: Build result dynamically from PROVIDER_METADATA keys
    const result: Record<string, boolean> = {};
    for (const key of Object.keys(PROVIDER_METADATA)) {
      const meta = PROVIDER_METADATA[key];
      result[key] = providers?.some((p: { provider_type: string; is_active: boolean }) =>
        (p.provider_type === meta.type || p.provider_type === key) && p.is_active
      ) ?? false;
    }

    return result as Record<PaymentProvider, boolean>;
  } catch (error) {
    if (import.meta.env.DEV) console.error('Error in getPaymentProviderSettings:', error);
    return { transbank: false, mercadopago: false };
  }
}

/**
 * Get all payment providers for a tenant schema (used by PaymentSettings page)
 */
export async function getAllPaymentProviders(
  schemaName: string
): Promise<Array<{ provider_name: string; provider_type: string; configuration: Record<string, unknown>; is_active: boolean; test_mode: boolean }>> {
  try {
    const { data, error } = await supabase.rpc('get_tenant_payment_providers', {
      p_tenant_schema: schemaName,
    });

    if (error) {
      if (import.meta.env.DEV) console.error('Error fetching all providers:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    if (import.meta.env.DEV) console.error('Error in getAllPaymentProviders:', error);
    return [];
  }
}

/**
 * Save payment provider settings via upsert RPC
 */
export async function savePaymentProviderSettings(
  tenantId: string,
  provider: PaymentProvider,
  settings: Record<string, unknown>
): Promise<{ success: boolean; error?: string }> {
  try {
    // Look up schema_name from tenants table
    const { data: tenant, error: tenantError } = await supabase
      .from('tenants')
      .select('schema_name')
      .eq('id', tenantId)
      .single();

    if (tenantError || !tenant?.schema_name) {
      return { success: false, error: 'Tenant not found' };
    }

    // G2: Use PROVIDER_METADATA for name/type resolution
    const meta = PROVIDER_METADATA[provider];
    const providerName = meta?.name || provider;
    const providerType = meta?.type || provider;

    // Configuration is provider-specific — each has different fields
    let configuration: Record<string, unknown>;
    if (provider === 'transbank') {
      configuration = {
        commerce_code: settings.commerce_code || '',
        api_key: settings.api_key || '',
        return_url: settings.return_url || '',
        payment_mode: settings.payment_mode || 'sandbox',
        pre_production_whitelist: settings.pre_production_whitelist || [],
        test_commerce_code: settings.test_commerce_code || '',
        test_api_key: settings.test_api_key || '',
      };
    } else {
      configuration = {
        access_token: settings.access_token || '',
        test_access_token: settings.test_access_token || '',
        notification_url: settings.notification_url || '',
        // Pre-production mode fields
        payment_mode: settings.payment_mode || 'sandbox',
        pre_production_whitelist: settings.pre_production_whitelist || [],
        checkout_mode: settings.checkout_mode || 'standard',
        max_installments: settings.max_installments || '',
        default_installments: settings.default_installments || '',
        link_expiration_minutes: settings.link_expiration_minutes || '60',
        excluded_payment_types: settings.excluded_payment_types || [],
        binary_mode: settings.binary_mode || false,
        // Phase 2: fields backend already reads
        webhook_secret: settings.webhook_secret || '',
        webhook_signature_mode: settings.webhook_signature_mode || 'permissive',
        default_currency: settings.default_currency || 'CLP',
        category_id: settings.category_id || '',
        integrator_id: settings.integrator_id || '',
        platform_id: settings.platform_id || '',
        // Phase 3: LATAM readiness
        default_dni_type: settings.default_dni_type || 'RUT',
      };
    }

    const { error } = await supabase.rpc('upsert_tenant_payment_provider', {
      p_tenant_schema: tenant.schema_name,
      p_provider_name: providerName,
      p_provider_type: providerType,
      p_configuration: configuration,
      // Was hardcoded `true`. A judge drove the space wizard on 2026-08-24, switched a provider
      // OFF, and the write reported success while setting is_active back to true -- the screen
      // said off and the database said active, with only updated_at moving. Defaulting to true
      // keeps every existing caller (which never passes it, and does mean "save and enable")
      // behaving exactly as before, while a caller that has an intent can now state it.
      p_is_active: typeof settings.is_active === 'boolean' ? settings.is_active : true,
      p_test_mode: settings.test_mode ?? true,
    });

    if (error) {
      if (import.meta.env.DEV) console.error('Error saving provider settings:', error);
      return { success: false, error: getErrorMessage(error) || 'Failed to save settings' };
    }

    return { success: true };
  } catch (error: unknown) {
    if (import.meta.env.DEV) console.error('Error in savePaymentProviderSettings:', error);
    return {
      success: false,
      error: getErrorMessage(error) || 'An unexpected error occurred',
    };
  }
}

/**
 * Toggle payment provider active state without changing credentials.
 * Used by provider enable/disable switch in PaymentSettings page.
 */
export async function togglePaymentProvider(
  tenantId: string,
  provider: PaymentProvider,
  isActive: boolean
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: tenant, error: tenantError } = await supabase
      .from('tenants')
      .select('schema_name')
      .eq('id', tenantId)
      .single();

    if (tenantError || !tenant?.schema_name) {
      return { success: false, error: 'Tenant not found' };
    }

    // Fetch existing provider to preserve configuration
    const allProviders = await getAllPaymentProviders(tenant.schema_name);
    const meta = PROVIDER_METADATA[provider];
    const existing = allProviders.find(
      (p) => p.provider_type === meta?.type || p.provider_type === provider
    );

    if (!existing) {
      return { success: false, error: 'Provider not configured. Save credentials first.' };
    }

    const { error } = await supabase.rpc('upsert_tenant_payment_provider', {
      p_tenant_schema: tenant.schema_name,
      p_provider_name: existing.provider_name,
      p_provider_type: existing.provider_type,
      p_configuration: existing.configuration,
      p_is_active: isActive,
      p_test_mode: existing.test_mode,
    });

    if (error) {
      if (import.meta.env.DEV) console.error('Error toggling provider:', error);
      return { success: false, error: getErrorMessage(error) || 'Failed to toggle provider' };
    }

    return { success: true };
  } catch (error: unknown) {
    if (import.meta.env.DEV) console.error('Error in togglePaymentProvider:', error);
    return {
      success: false,
      error: getErrorMessage(error) || 'An unexpected error occurred',
    };
  }
}

/**
 * Delete (soft-disable) payment provider settings via upsert with is_active=false
 */
export async function deletePaymentProviderSettings(
  tenantId: string,
  provider: PaymentProvider
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: tenant, error: tenantError } = await supabase
      .from('tenants')
      .select('schema_name')
      .eq('id', tenantId)
      .single();

    if (tenantError || !tenant?.schema_name) {
      return { success: false, error: 'Tenant not found' };
    }

    // G2: Use PROVIDER_METADATA for name/type resolution
    const deleteMeta = PROVIDER_METADATA[provider];
    const providerName = deleteMeta?.name || provider;
    const providerType = deleteMeta?.type || provider;

    const { error } = await supabase.rpc('upsert_tenant_payment_provider', {
      p_tenant_schema: tenant.schema_name,
      p_provider_name: providerName,
      p_provider_type: providerType,
      p_configuration: {},
      p_is_active: false,
      p_test_mode: true,
    });

    if (error) {
      if (import.meta.env.DEV) console.error('Error deleting provider settings:', error);
      return { success: false, error: 'Failed to delete provider settings' };
    }

    return { success: true };
  } catch (error: unknown) {
    if (import.meta.env.DEV) console.error('Error in deletePaymentProviderSettings:', error);
    return {
      success: false,
      error: getErrorMessage(error) || 'An unexpected error occurred',
    };
  }
}

/**
 * Cancel a payment (mark as expired/cancelled)
 */
export async function cancelPayment(
  tenantId: string,
  paymentId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: tenant, error: tenantError } = await supabase
      .from('tenants')
      .select('schema_name')
      .eq('id', tenantId)
      .single();

    if (tenantError || !tenant) {
      return { success: false, error: 'Tenant not found' };
    }

    const { error } = await supabase.rpc('expire_payment', {
      p_tenant_schema: tenant.schema_name,
      p_payment_id: paymentId,
    });

    if (error) {
      if (import.meta.env.DEV) console.error('Error cancelling payment:', error);
      return { success: false, error: 'Failed to cancel payment' };
    }

    return { success: true };
  } catch (error: unknown) {
    if (import.meta.env.DEV) console.error('Error in cancelPayment:', error);
    return {
      success: false,
      error: getErrorMessage(error) || 'An unexpected error occurred',
    };
  }
}

/**
 * Format amount for display
 */
export function formatPaymentAmount(amount: number, currency: string = 'CLP'): string {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency,
  }).format(amount);
}

/**
 * Get payment status display text (i18n-aware)
 */
export function getPaymentStatusText(status: string, t?: (key: string) => string): string {
  if (t) {
    return t(`payments:status.${status}`) || status;
  }
  // Fallback to hardcoded Spanish (legacy behavior)
  const statusMap: Record<string, string> = {
    pending: 'Pendiente',
    completed: 'Completado',
    failed: 'Fallido',
    expired: 'Expirado',
    cancelled: 'Cancelado',
    refunded: 'Reembolsado',
  };
  return statusMap[status] || status;
}

/**
 * Get payment provider display name
 * G2: Uses PROVIDER_METADATA instead of hardcoded map
 */
export function getPaymentProviderName(provider: PaymentProvider): string {
  return PROVIDER_METADATA[provider]?.name || provider;
}
